// To show a new announcement, update `id` and the release content before deploying.
// To disable the banner, set `enabled` to false.
// `releases` is the full history — the first entry is always the current release shown in the banner.

const announcements = {
    enabled: true,
    current: "2026-08-22-comments-feedback",
    releases: [
        {
            id: "2026-08-22-comments-feedback",
            title: "Comments & In-App Feedback",
            date: "August 22, 2026",
            summary: "Leave notes on recipes with a new comment section, and send feedback or bug reports directly from the app.",
            features: [
                {
                    icon: "💬",
                    title: "Recipe Comments",
                    description: "Every recipe now has a comment section at the bottom. Leave notes, tips, or tweaks — replies are supported so you can respond to a specific comment.",
                },
                {
                    icon: "📣",
                    title: "In-App Feedback",
                    description: "Have a suggestion or found a bug? Use the new Feedback option in the menu to send it directly — no email needed.",
                },
            ],
        },
        {
            id: "2026-07-11-multiplier-delete",
            title: "Share, Export, Custom Scaling & More",
            date: "July 11, 2026",
            summary: "Share recipes with anyone, export to PDF, scale to any amount, admin recipe deletion, and a round of UI polish throughout the app.",
            features: [
                {
                    icon: "🔗",
                    title: "Share with Anyone",
                    description: "Generate a public link for any recipe so friends and family outside the app can view it — no account required. Links can be revoked at any time.",
                },
                {
                    icon: "🖨️",
                    title: "Export to PDF",
                    description: "Export any recipe as a print-ready PDF. Choose your font size, single or two-column layout, and whether to include the photo — with a live preview before you print.",
                },
                {
                    icon: "📚",
                    title: "Add to Collection from Recipe Page",
                    description: "A new collections button sits right next to the favorites heart on every recipe — tap it to add the recipe to any of your collections without opening a menu.",
                },
                {
                    icon: "✖️",
                    title: "Custom Recipe Multiplier",
                    description: "Beyond 1x, 2x, and 3x — tap Custom to enter any multiplier you need, like 0.5x for a half batch or 10x for a crowd. Values snap to quarter increments to keep the math clean.",
                },
                {
                    icon: "🗑️",
                    title: "Recipe Deletion (Admin)",
                    description: "Admins can now remove a recipe from the cookbook without permanently deleting it. Deleted recipes are hidden from all users and can be restored anytime from the Admin page.",
                },
                {
                    icon: "✨",
                    title: "UI Polish",
                    description: "Dialogs throughout the app got a consistent refresh with icons and clearer titles. Icon buttons now show tooltips on hover so it's always clear what they do.",
                },
            ],
        },
        {
            id: "2026-05-06-invite-and-rebrand",
            title: "New Look & Invite-Only Signup",
            date: "May 6, 2026",
            summary: "A fresh coat of paint for the whole app, plus a proper invite system so family members can join on their own.",
            features: [
                {
                    icon: "✉️",
                    title: "Invite Family Members",
                    description: "Admins can now send invite links directly from the Admin page. Recipients get a branded email and can create their own account — no manual setup required.",
                },
                {
                    icon: "🎨",
                    title: "New Look",
                    description: "The app has a new crimson red theme throughout — inspired by the Mangia! Mangia! logo. Login, signup, and the navbar all got the full treatment.",
                },
                {
                    icon: "🖼️",
                    title: "App Icon",
                    description: "The Mangia! Mangia! icon now shows up in your browser tab, home screen shortcut, and throughout the app.",
                },
            ],
        },
        {
            id: "2026-05-05-cook-session",
            title: "Cooking Sessions",
            date: "May 5, 2026",
            summary: "Follow along while you cook — check off ingredients and steps, scale recipes, and keep multiple recipes going at once.",
            features: [
                {
                    icon: "🍳",
                    title: "Cook Mode",
                    description: "Open any recipe and tap 'Start Cooking' to enter a cooking session. Check off ingredients and directions as you go, and your progress is saved for up to 12 hours.",
                },
                {
                    icon: "📋",
                    title: "Multiple Recipes at Once",
                    description: "Add as many recipes as you need to a single session — great for cooking a full meal. Switch between them from the sidebar or the session chip at the top.",
                },
                {
                    icon: "✖️",
                    title: "Scale Ingredients",
                    description: "Tap 1x, 2x, or 3x next to the ingredients to scale quantities up. Each recipe in your session can have its own multiplier.",
                },
                {
                    icon: "📱",
                    title: "Screen Stays On",
                    description: "Your screen won't lock while you're in a cooking session, so you won't lose your place mid-recipe.",
                },
            ],
        },
        {
            id: "2026-05-04-search-and-fixes",
            title: "People Search, Section Ordering & Fixes",
            date: "May 4, 2026",
            summary: "Search for family members and authors by name, ingredient and direction sections now stay in the right order, and a handful of bug fixes.",
            features: [
                {
                    icon: "🔍",
                    title: "Search for People",
                    description: "You can now search for family members and author profiles by name. Results show up alongside recipes — tap a person to go straight to their page.",
                },
                {
                    icon: "📋",
                    title: "Filter Recipes on Author & Profile Pages",
                    description: "Author and user profile pages now have a small search box so you can quickly find a specific recipe from their list.",
                },
                {
                    icon: "🗂️",
                    title: "Sections Stay in Order",
                    description: "Ingredient and direction sections now appear in the exact order you arranged them, not alphabetically.",
                },
            ],
        },
        {
            id: "2026-05-03-multi-author",
            title: "Multiple Authors & Home Search",
            date: "May 3, 2026",
            summary: "Recipes can now credit multiple authors, and there's a new search bar right on the home page.",
            features: [
                {
                    icon: "👨‍👩‍👧",
                    title: "Multiple Authors per Recipe",
                    description: "Recipes can now have more than one author. Add family members, author profiles, or any free-text name — they'll all appear together on the recipe page.",
                },
                {
                    icon: "🔍",
                    title: "Search on the Home Page",
                    description: "A search bar now sits right at the top of the home page so you can find a recipe without navigating away first.",
                },
                {
                    icon: "🔎",
                    title: "Smarter Author Search",
                    description: "Searching by author name now finds recipes across all their credited authors, not just the primary one.",
                },
            ],
        },
        {
            id: "2026-04-29-author-pages-and-import",
            title: "Author Pages, Recipe Import & More",
            date: "April 29, 2026",
            summary: "Author profiles, import recipes from the web, public user pages, and faster images.",
            features: [
                {
                    icon: "👨‍🍳",
                    title: "Author Profiles",
                    description: "Recipes can now be credited to a named author with their own page showing a bio, photo, and all their recipes.",
                },
                {
                    icon: "🔗",
                    title: "Clickable Author Links",
                    description: "Author names on recipes are now clickable links that take you directly to that author's page.",
                },
                {
                    icon: "👤",
                    title: "Public User Pages",
                    description: "Every family member now has a public profile page showing all the recipes they've contributed. Find it via your profile page.",
                },
                {
                    icon: "🌐",
                    title: "Import Recipes from a URL",
                    description: "Paste a link from AllRecipes, Food Network, NYT Cooking, and hundreds of other sites — the recipe will be prefilled as a draft ready for you to review and save.",
                },
                {
                    icon: "⚡",
                    title: "Faster Image Loading",
                    description: "Recipe images now load from a global CDN, so they appear faster no matter where you are.",
                },
                {
                    icon: "🛠️",
                    title: "Admin Panel",
                    description: "A new dedicated Admin page makes it easier to manage authors and family members (editors and admins only).",
                },
            ],
        },
        {
            id: "2026-04-27-profiles",
            title: "Profiles, Dark Mode & Permissions",
            date: "April 27, 2026",
            summary: "Personal profiles with avatars, dark mode, and smarter recipe permissions.",
            features: [
                {
                    icon: "👤",
                    title: "User Profiles",
                    description: "Set a profile photo, display name, and bio. Edit your profile and change your password directly from the profile page.",
                },
                {
                    icon: "🌙",
                    title: "Dark Mode",
                    description: "Toggle dark mode in Settings. Your preference is saved and follows you across devices.",
                },
                {
                    icon: "🔒",
                    title: "Recipe Permissions",
                    description: "Recipes are now tied to who added them. You can always edit your own recipes.",
                },
                {
                    icon: "👨‍👩‍👧",
                    title: "Family Filter",
                    description: "A new filter in Settings lets you show only family recipes, so the cookbook stays close to home.",
                },
            ],
        },
        {
            id: "2026-04-26-drafts",
            title: "Draft Recipes & Reordering",
            date: "April 26, 2026",
            summary: "Save recipe drafts as you go and drag to reorder ingredients and directions.",
            features: [
                {
                    icon: "📝",
                    title: "Draft Recipes",
                    description: "Start a recipe and save it as a draft. Come back to it anytime, even from a different device. Up to 5 drafts saved at once.",
                },
                {
                    icon: "🔀",
                    title: "Drag to Reorder",
                    description: "Rearrange ingredients and directions by dragging them into the right order — no more copy-pasting to fix a skipped step.",
                },
                {
                    icon: "📂",
                    title: "Reorder Sections",
                    description: "Got multiple ingredient or direction sections? Drag the whole section to move it up or down.",
                },
            ],
        },
        {
            id: "2026-04-25-collections",
            title: "Collections are here!",
            date: "April 25, 2026",
            summary: "Save and organize your favorite recipes into custom collections.",
            features: [
                {
                    icon: "📚",
                    title: "Collections",
                    description: "Create custom collections to group and organize your recipes however you like.",
                },
                {
                    icon: "❤️",
                    title: "Favorites",
                    description: "Heart any recipe to instantly save it to your personal Favorites collection.",
                },
                {
                    icon: "🤝",
                    title: "Sharing",
                    description: "Share collections with family members. Grant view-only or full edit access.",
                },
                {
                    icon: "✏️",
                    title: "Collaborate",
                    description: "Collaborators with edit access can add and remove recipes from shared collections.",
                },
            ],
        },
        {
            id: "2026-04-19-firebase-fixes",
            title: "Image Upload Fix",
            date: "April 19, 2026",
            summary: "Fixed image uploads and improved recipe data freshness.",
            features: [
                {
                    icon: "🖼️",
                    title: "Image Uploads Fixed",
                    description: "Resolved an issue where recipe images were not uploading correctly in some cases.",
                },
                {
                    icon: "⚡",
                    title: "Faster Recipe Data",
                    description: "Recipe data now refreshes automatically every 10 minutes so you always see the latest additions.",
                },
            ],
        },
        {
            id: "2026-04-15-fastapi-recipe-edit",
            title: "Recipe Editing & Backend Upgrade",
            date: "April 15, 2026",
            summary: "Edit recipes directly in the app and a full backend overhaul under the hood.",
            features: [
                {
                    icon: "📝",
                    title: "Recipe Editing",
                    description: "Edit any recipe's ingredients, directions, and photos directly from the recipe page.",
                },
                {
                    icon: "🕓",
                    title: "Recipe History",
                    description: "Every edit is saved — browse the full version history of any recipe.",
                },
                {
                    icon: "🔧",
                    title: "Backend Upgrade",
                    description: "Migrated to a faster, more reliable backend to keep things running smoothly.",
                },
            ],
        },
    ],
};

export default announcements;
